import { generateTestId } from "@client-shared";
import type { PurchaseOrderLine } from "@shared/types";

export const setOrderDetailNoticeTestId = (identifier: string): string => generateTestId("OrderDetailNotice", identifier);

/**
 * Safely parses a JSON error string. Returns the `message` field if present,
 * otherwise falls back to the raw string.
 *
 * Keep the unwrapping rules in sync with extractLabelErrorMessage in
 * shared/src/lib/logHelper.ts, which surfaces the same reason in Slack alerts —
 * the frontend/backend boundary prevents importing the backend copy here.
 */
export function getErrorMessage(error: string | undefined): string {
  if (!error) return "";
  try {
    const sanitised = error.replace(/^Error:\s*/i, "");
    const parsed: unknown = JSON.parse(sanitised);
    if (parsed !== null && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      // Zenkraft payloads vary between "details" (plural) and "detail"
      // (singular); prefer the most specific before the top-level message.
      const data = obj["data"] as Record<string, unknown> | undefined;
      const dataError = data?.["error"] as Record<string, unknown> | undefined;
      if (typeof dataError?.["details"] === "string") {
        return dataError["details"];
      }
      if (typeof dataError?.["detail"] === "string") {
        return dataError["detail"];
      }
      // data.error.message
      if (typeof dataError?.["message"] === "string") {
        return dataError["message"];
      }
      // top-level message
      if (typeof obj["message"] === "string") return obj["message"];
    }
  } catch {
    // not JSON — fall through
  }
  return error;
}

/**
 * Collects a deduplicated list of error messages from the PO-level error
 * and any line-item errors. Parsed via getErrorMessage so JSON strings are
 * unwrapped before deduplication.
 */
export function collectOrderErrors(poErrorMessage: string | undefined, lineItems: PurchaseOrderLine[]): string[] {
  const seen = new Set<string>();
  const errors: string[] = [];

  const add = (raw: string | undefined): void => {
    const msg = getErrorMessage(raw);
    if (msg && !seen.has(msg)) {
      seen.add(msg);
      errors.push(msg);
    }
  };

  add(poErrorMessage);
  for (const li of lineItems) {
    add(li.errorMessage);
  }

  return errors;
}
